/**
 * Reads DPI metadata from a PNG or JPEG file using raw byte parsing.
 * Returns { dpi: number|null, message: string }
 *
 * PNG: reads the pHYs chunk (pixels per meter → DPI)
 * JPEG: reads the JFIF APP0 marker (density units + x/y density)
 */

function readUint32BE(bytes, offset) {
  return ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0;
}

function readUint16BE(bytes, offset) {
  return (bytes[offset] << 8) | bytes[offset + 1];
}

function parsePNGDPI(bytes) {
  let offset = 8; // skip 8-byte PNG signature
  while (offset < bytes.length - 12) {
    const length = readUint32BE(bytes, offset);
    const type = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);

    if (type === "pHYs" && length >= 9) {
      const pixelsPerUnitX = readUint32BE(bytes, offset + 8);
      const unit = bytes[offset + 16]; // 0=unknown, 1=meter
      if (unit === 1 && pixelsPerUnitX > 0) {
        const dpi = Math.round(pixelsPerUnitX / 39.3701);
        return { dpi, message: `${dpi} DPI` };
      }
      return { dpi: null, message: "DPI not set (no unit in pHYs)" };
    }

    if (type === "IDAT" || type === "IEND") break;
    offset += 12 + length; // length(4) + type(4) + data(length) + crc(4)
  }
  return { dpi: null, message: "No DPI metadata found" };
}

function parseJPEGDPI(bytes) {
  let offset = 2; // skip FF D8
  while (offset < bytes.length - 4) {
    if (bytes[offset] !== 0xff) break;
    const marker = bytes[offset + 1];
    if (offset + 3 >= bytes.length) break;
    const segmentLength = readUint16BE(bytes, offset + 2);

    // APP0 (JFIF): FF E0
    if (marker === 0xe0 && segmentLength >= 16) {
      const isJFIF =
        bytes[offset + 4] === 0x4a && // J
        bytes[offset + 5] === 0x46 && // F
        bytes[offset + 6] === 0x49 && // I
        bytes[offset + 7] === 0x46;   // F
      if (isJFIF) {
        const units = bytes[offset + 11];
        const xDensity = readUint16BE(bytes, offset + 12);
        if (units === 1 && xDensity > 0) {
          return { dpi: xDensity, message: `${xDensity} DPI` };
        } else if (units === 2 && xDensity > 0) {
          const dpi = Math.round(xDensity * 2.54);
          return { dpi, message: `${dpi} DPI` };
        }
        return { dpi: null, message: "DPI not set in JFIF header" };
      }
    }

    offset += 2 + segmentLength;
  }
  return { dpi: null, message: "No DPI metadata found" };
}

export async function checkImageDPI(file) {
  try {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // PNG: 89 50 4E 47
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      return parsePNGDPI(bytes);
    }

    // JPEG: FF D8
    if (bytes[0] === 0xff && bytes[1] === 0xd8) {
      return parseJPEGDPI(bytes);
    }

    return { dpi: null, message: "Unsupported format (PNG or JPEG required)" };
  } catch {
    return { dpi: null, message: "Could not read file" };
  }
}
